import { useEffect, useState } from "react";
import { ShieldAlert, FlaskConical } from "lucide-react";

// Age-gate acceptance is intentionally SESSION-scoped, not persisted forever.
// Every new browser session must re-attest — this is the compliant default
// for RUO peptide sales and avoids "silent" onboarding of anyone who reuses
// a shared device or comes back weeks later.
const STORAGE_KEY = "nebula_age_gate_v1";

export function AgeGate() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const ok = sessionStorage.getItem(STORAGE_KEY);
    if (!ok) setOpen(true);
    // Clean up any legacy long-lived localStorage flag so it can never bypass
    // the session gate on future visits.
    if (localStorage.getItem(STORAGE_KEY)) localStorage.removeItem(STORAGE_KEY);
    if (localStorage.getItem("helix_age_gate_v1")) localStorage.removeItem("helix_age_gate_v1");
  }, []);

  if (!open) return null;

  const accept = () => {
    sessionStorage.setItem(STORAGE_KEY, "1");
    setOpen(false);
  };

  return (
    <div className="fixed inset-0 z-[100] bg-[var(--paper)]/80 backdrop-blur-md grid place-items-center p-4 fade-in" data-testid="age-gate">
      {/* Nebula glow blobs behind modal */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="nebula-blob cyan" style={{ width: 380, height: 380, top: '12%', left: '8%' }} />
        <div className="nebula-blob magenta" style={{ width: 320, height: 320, bottom: '8%', right: '10%' }} />
        <div className="nebula-blob violet" style={{ width: 260, height: 260, top: '40%', right: '32%' }} />
      </div>
      <div className="relative max-w-lg w-full bg-[var(--paper-2)] border border-[var(--line)] rounded-xl shadow-2xl scale-in overflow-hidden">
        <div className="absolute inset-0 starfield opacity-40 pointer-events-none" />
        <div className="relative bg-[var(--paper-3)] border-b border-[var(--line)] px-6 py-4 flex items-center gap-3">
          <FlaskConical className="h-5 w-5 text-[var(--terra)]" strokeWidth={1.6} />
          <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--terra)]">Research Use Notice</div>
        </div>
        <div className="relative px-6 py-7">
          <div className="font-display text-3xl tracking-tight mb-4 gradient-text">You must be 21 or older to enter.</div>
          <p className="text-sm text-[var(--ink-soft)] leading-relaxed">
            All products are sold for <strong className="text-[var(--ink)]">laboratory research use only</strong> and are not for human or animal consumption.
          </p>
          <ul className="mt-4 space-y-2 text-sm text-[var(--ink-soft)] list-disc list-outside ml-5 marker:text-[var(--terra)]">
            <li>Products are not drugs, foods, dietary supplements, cosmetics, or medical devices.</li>
            <li>They have not been evaluated by the FDA for safety or efficacy.</li>
            <li>You accept responsibility for compliance with all applicable laws and good laboratory practice.</li>
          </ul>
          <p className="mt-5 text-[11px] font-mono uppercase tracking-[0.16em] text-[var(--ink-muted)]">
            You'll be asked to re-verify on your next visit.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <button
              onClick={accept}
              className="btn-primary justify-center w-full sm:w-auto"
              data-testid="age-gate-accept"
            >
              <ShieldAlert className="h-4 w-4" /> Enter — I am 21 or older
            </button>
            <a href="https://www.google.com" className="btn-outline justify-center w-full sm:w-auto" data-testid="age-gate-decline">
              Leave
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
